import React from "react";
import SEO from "../components/SEO";
import { openPrivacyChoices } from "../components/CookieChoices";

export default function Privacy() {
  return (
    <section className="page container">
      {" "}
      <SEO
        title="BossDrop | Privacy Policy"
        description="BossDrop Privacy Policy: how we collect, use, and protect data, retention practices, your choices, and how to contact us."
        canonical="https://bossdrop.ai/privacy"
        url="https://bossdrop.ai/privacy"
        /* image="https://bossdrop.ai/og/pricing-1200x630.jpg" */
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Privacy",
              item: "https://bossdrop.ai/privacy",
            },
          ],
        }}
      />
      <div className="hero">
        <h1 className="h1">Privacy Policy</h1>
        <p className="sub">Last updated: November 4, 2026</p>
      </div>
      <div className="panel">
        <h2 className="h2">Overview</h2>
        <p>
          This Privacy Policy explains how BossDrop, Inc. (“BossDrop,” “we,”
          “our,” or “us”) collects, uses, and shares personal information when
          you visit our site or use our Service. For Customer Data that we
          process on behalf of an organization, we act as a processor; the
          organization is the controller and its instructions govern.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Information We Collect</h2>
        <ul className="bullets">
          <li>
            <strong>Account & Contact</strong>: name, email, role, org name,
            authentication identifiers, billing/admin details.
          </li>
          <li>
            <strong>Connected Tools</strong>: data you authorize us to access
            from the tools you connect: Slack (messages, channels, files,
            metadata); Microsoft Teams (channel and chat messages,
            participants, and meeting transcripts and attendance if your
            workspace turns on meeting notes); Outlook/Microsoft 365 (calendar
            events, attendees,
            availability, and email threads if you connect your mailbox for
            email tracking); Gmail, Google Calendar and Google Meet (calendar
            events, attendees, availability, email threads if you connect your
            mailbox for email tracking, and Meet transcripts and attendance if
            your workspace turns on meeting notes); Asana, Jira, Linear,
            ClickUp, Notion, monday.com and Microsoft Planner (tasks, projects,
            owners, due dates, comments); GitHub (pull requests, review
            requests, checks and commit counts, plus short code excerpts only when an admin
            uses Ask about the code); HubSpot (deals and pipeline, and the
            contacts on a deal if you approve that access); and Pipedrive (deals
            and pipelines, the person and organization on a deal, and your
            Pipedrive users&apos; names and email addresses). Access is limited
            to scopes you approve and can be revoked anytime.
          </li>
          <li>
            <strong>Team Memory</strong>: timing and people from coordination
            in your connected tools, such as when someone was asked something
            and when they answered. See Team Memory below.
          </li>
          <li>
            <strong>Usage & Device</strong>: logs, actions, automation runs,
            timestamps, IP address, device/browser, app configuration, crash
            reports, and performance data.
          </li>
          <li>
            <strong>Content You Provide</strong>: prompts, notes, uploaded
            files, and any data entered into the Service.
          </li>
          <li>
            <strong>Payments</strong>: handled by our payment processor (e.g.,
            Stripe). We do not store full card numbers.
          </li>
          <li>
            <strong>Cookies & Website Tools</strong>: we use cookies to operate
            the Service, and cookies and the tools named under Tools on Our
            Website to understand and improve our website, advertise, and learn
            who visits it. Manage cookies via your browser settings.
          </li>
          <li>
            <strong>Implementation specialist links</strong>: if you arrive
            through a specialist&apos;s link, we keep their code in your browser
            for 90 days after your latest visit and include it with any demo
            booking or form you send us, so we can credit them. They can see your
            name and company if you book a demo that way, and whether you became a
            customer. Where we ask before running the tools on our website, we
            keep the code only until you leave the page, unless you allow it.
          </li>
          <li>
            <strong>Implementation specialists</strong>: if you join the program
            we keep your name, email, the companies you register and your
            earnings, and we use a cookie to keep you signed in to your dashboard.
            The sign-in and dashboard pages load no analytics tools.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Tools on Our Website</h2>
        <p>
          The tools below run in your browser when you visit bossdrop.ai, on the
          terms set out after this list, and send what they collect straight to
          the companies that provide them.
          They use cookies or similar browser storage, and those companies
          handle the data under their own privacy policies. They do not run in
          the BossDrop app, or on the specialist sign-in and dashboard pages.
        </p>
        <ul className="bullets">
          <li>
            <strong>Google Analytics (Google)</strong>: counts page views and
            actions on the site, such as scrolling, clicking Book a demo and
            booking a demo, along with your device, browser, approximate
            location and the site that sent you here.
          </li>
          <li>
            <strong>Microsoft Clarity (Microsoft)</strong>: records how people
            use our pages, including clicks, scrolling and mouse movement, as
            heatmaps and session recordings. Anything you type is masked.
          </li>
          <li>
            <strong>Meta Pixel (Meta)</strong>: tells Meta when you view a page,
            click a button or Book a demo, book a demo, or sign up through a
            form, along with your IP address, browser details and the page
            address. Meta can connect this to a Facebook or Instagram account.
            We use it to measure our advertising and to show ads to people who
            have visited our site.
          </li>
          <li>
            <strong>Visitor identification (Instantly, using RB2B)</strong>:
            tries to work out which person and company is behind a visit, so we
            can follow up with people who may be interested in BossDrop. It
            looks up your IP address and location, reads cookies including
            Meta&apos;s, and checks identity-data providers, including
            LiveIntent, to match your browser to a hashed email address. A match
            can give us details such as your name, business email address, job
            title, company and LinkedIn profile. It is set to identify people
            only on visits from the United States.
          </li>
        </ul>
        <p>
          If your IP address places you in Europe, including the EU and UK, these
          tools stay off until you allow them, and refusing is as easy as
          allowing. If we can&apos;t tell from your IP address, we go by your
          device&apos;s time zone, and they also stay off if it is in Europe or
          says nothing about where you are. Everywhere else they run unless you
          turn them off. We use your IP address for this only to find your
          country, and we don&apos;t store the answer. We treat Global Privacy
          Control, a setting some browsers send, as turning all of them off.
        </p>
        <p>
          {window.bdNoTrack ? "They are turned off here. " : null}Use{" "}
          <a
            href="/privacy"
            onClick={openPrivacyChoices}
            style={{ textDecoration: "underline" }}
          >
            Your privacy choices
          </a>
          {" "}to allow or refuse each kind at any time. Your choice stays in your
          browser and lasts 12 months. To ask what we
          hold about you or have it deleted, write to{" "}
          <a href="mailto:support@bossdrop.ai">support@bossdrop.ai</a>.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Email Tracking (Gmail and Outlook)</h2>
        <p>
          Email tracking is optional. Nothing in a mailbox is read until its
          owner connects it.
        </p>
        <ul className="bullets">
          <li>
            <strong>Who connects</strong>: each person connects only their own
            Gmail or Outlook mailbox, from Control Room in the BossDrop app.
            No one can connect another person&apos;s mailbox.
          </li>
          <li>
            <strong>What we read</strong>: read-only access to recent email,
            normally the last 14 days. BossDrop searches only for threads that
            involve
            BossDrop, your team&apos;s addresses, or the domains and addresses
            your workspace chose to track, and ignores all other email. It
            never sends email from your mailbox and never changes or deletes
            your messages.
          </li>
          <li>
            <strong>How we use it</strong>: as context for the work you set
            BossDrop up to do, such as status follow-ups, escalations, tasks,
            meetings and the executive report. Thread text is sent to your
            workspace&apos;s AI provider to plan those actions and write
            messages. BossDrop also notes when a reply arrived, not what it
            said, to learn how quickly people answer (see Team Memory).
          </li>
          <li>
            <strong>What we keep</strong>: matching threads are read again on
            each run. BossDrop keeps excerpts of them in your workspace&apos;s
            memory so it can recall earlier context, and deletes each excerpt
            automatically after the period set in Control Room: 30 days by
            default, which a workspace admin can extend. Facts and summaries
            BossDrop learned from them stay in its memory until an admin clears
            it on the Memory page. We also delete it on request, and when a
            workspace is deleted.
          </li>
          <li>
            <strong>Stopping</strong>: stop BossDrop reading your mailbox at
            any time in Control Room. To remove access entirely, remove
            BossDrop from the third-party apps in your Google Account, or from
            the apps in your Microsoft account.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Meeting Notes (Microsoft Teams and Google Meet)</h2>
        <p>
          Meeting notes are optional and stay off until a workspace admin turns
          them on and accepts the terms in Control Room. They apply only to
          meetings that BossDrop schedules while the setting is on.
        </p>
        <ul className="bullets">
          <li>
            <strong>How it works</strong>: BossDrop never joins or records a
            call. After the meeting, it reads the transcript that Microsoft
            Teams or Google Meet made, using the organizer&apos;s own
            connection. For Teams, your Microsoft 365 admin approves this
            access. For Google Meet, the organizer approves it, and can also
            let BossDrop turn on Meet&apos;s transcription for the meetings
            BossDrop schedules.
          </li>
          <li>
            <strong>What we read</strong>: the transcript (who spoke, what they
            said and when) and which of the invited people joined. Attendance
            is matched only against the invitee list. BossDrop does not access
            audio or video recordings.
          </li>
          <li>
            <strong>Notice and opt-out</strong>: the calendar invite says the
            meeting will have AI notes, and every invitee is also emailed from
            ai@bossdrop.ai with a personal opt-out link that works without
            signing in. BossDrop reads a transcript only after every invitee
            has been told. If anyone opts out, BossDrop does not read that
            meeting&apos;s transcript, deletes any copy it already stored, and
            tells the organizer without saying who opted out.
          </li>
          <li>
            <strong>Meetings we skip</strong>: meetings whose titles suggest
            HR, legal, one-on-one, performance, pay, termination, health or
            interview topics (an admin can change this), meetings with more
            than 50 invitees, and meetings booked less than 15 minutes before
            they start.
          </li>
          <li>
            <strong>How we use it</strong>: the transcript is sent to your
            workspace&apos;s AI provider to write notes and follow-up items
            such as tasks, which follow your workspace&apos;s approval
            settings. Notes go to the organizer and to internal invitees who
            joined. Guests from outside your organization get a version without
            internal items, and only if a workspace admin approves it.
          </li>
          <li>
            <strong>What we keep</strong>: transcripts are stored encrypted and
            deleted automatically 30 days after BossDrop collects them. Notes,
            also stored encrypted, and the meeting&apos;s details (title, time,
            invitees, who joined and who received notes) stay in your
            workspace until it is deleted, or until you ask us to delete them.
          </li>
          <li>
            <strong>Stopping</strong>: a workspace admin can turn meeting notes
            off at any time in Control Room, and BossDrop then reads no more
            transcripts. Transcripts already stored are still deleted on the
            30-day schedule. To remove access entirely, remove BossDrop from
            the apps in your Google Account, or ask your Microsoft 365 admin to
            remove it.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Team Memory (How Your Team Runs)</h2>
        <p>
          As BossDrop works, it learns how your team runs and keeps that in one
          structured document, rewritten once a day from real work. It uses the
          document to decide who to ask, when to check in, and how long to wait
          before going to someone&apos;s manager. People in your workspace can
          read it on the Memory page in the BossDrop app.
        </p>
        <ul className="bullets">
          <li>
            <strong>What we collect</strong>: moments of coordination, taken
            from the tasks and messages BossDrop already reads from your
            connected tools: who it asked and whether, how quickly and where
            they answered (including by email, if a mailbox is connected); how
            long BossDrop gave someone before going to their manager, which
            escalations got work moving and which people turned down; when tasks
            finished against their due dates; when due dates moved; when work changed hands; when blockers
            cleared; and how many chat messages each person sent in each hour of
            the day. We store people, times, project names and task references,
            not the text of any message. Email does not count toward a
            person&apos;s active hours.
          </li>
          <li>
            <strong>What else the document holds</strong>: settings that shape
            how BossDrop works, such as working hours and approval rules; how
            BossDrop&apos;s own actions turned out; facts and decisions people
            stated in your connected tools; and short summaries of recent weeks.
            Those facts and summaries are written from what people said.
          </li>
          <li>
            <strong>Who can see it</strong>: lines about one person, such as how
            quickly they answer, how often their work lands on its due date, or
            a working habit someone stated about them, are visible only to
            workspace admins and that person.
            Everything else, such as who mostly owns a project, how the team as
            a whole is running, other stated facts and weekly summaries, is visible to
            everyone in the workspace.
          </li>
          <li>
            <strong>How we use it</strong>: to time check-ins and escalations,
            and as context when BossDrop plans actions and writes messages and
            reports. The document is sent to your workspace&apos;s AI provider
            for this. BossDrop&apos;s AI is instructed not to quote one
            person&apos;s timing in a message or report. BossDrop does not rank
            people.
          </li>
          <li>
            <strong>Your organization&apos;s role</strong>: your organization
            decides whether to use BossDrop, and is responsible for telling its
            team and for following the employment and monitoring rules that
            apply to it. Team Memory is built to coordinate work, not to
            evaluate people, and should not be the basis for employment
            decisions.
          </li>
          <li>
            <strong>Correcting it</strong>: a workspace admin can correct any
            learned line in their own words, remove it, or add a new one, and a
            correction outranks what BossDrop measured. If a line about you
            looks wrong, ask an admin or write to{" "}
            <a href="mailto:support@bossdrop.ai">support@bossdrop.ai</a>.
          </li>
          <li>
            <strong>What we keep</strong>: moments are deleted automatically
            after 120 days. The record of which decisions used a line, including
            each decision&apos;s short title, is deleted after 180 days. The
            document, corrections and the history of what changed stay until an
            admin clears them or the workspace is deleted. Someone who leaves
            the team drops out of the document at its next daily rewrite.
          </li>
          <li>
            <strong>Exporting</strong>: a workspace admin can export all of it,
            as plain text or JSON, at any time from the Memory page. Each export
            is logged.
          </li>
          <li>
            <strong>Stopping</strong>: a workspace admin can clear what BossDrop
            has learned at any time on the Memory page: what it measured,
            corrections, what your team told it, and the history. It starts
            learning again from that day, because it learns for as long as it is
            connected to your tools. To stop it entirely, disconnect those tools
            or delete the workspace.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Custom Automations</h2>
        <p>
          Custom automations are optional. A workspace admin writes a workflow
          in their own words, and BossDrop does nothing with it until someone
          turns it on.
        </p>
        <ul className="bullets">
          <li>
            <strong>What we read</strong>: an automation reads your connected
            tools the way the rest of BossDrop does. One that starts from a deal
            in your CRM also reads that deal (title, link, value, stage,
            pipeline, owner, close date and next step) and the client on it
            (organization, contact name and email address). BossDrop never
            writes to your CRM.
          </li>
          <li>
            <strong>How we use it</strong>: the automation&apos;s words, that
            deal information and your team&apos;s names and email addresses are
            sent to your workspace&apos;s AI provider to plan the work, write
            messages and invites, and check the result.
          </li>
          <li>
            <strong>People outside your team</strong>: if your organization
            writes it into an automation, BossDrop can invite a client to
            meetings and email them from ai@bossdrop.ai. Your organization
            decides what it asks BossDrop to send, and is responsible for
            having a basis to contact them.
          </li>
          <li>
            <strong>What we keep</strong>: each run keeps that deal information,
            the client&apos;s name and email address, and a record of what
            BossDrop did, including the text of messages it sent, so the same
            deal is never set up twice. Runs stay until the workspace is
            deleted, or until you ask us to delete them. Deleting an automation
            does not delete its runs.
          </li>
          <li>
            <strong>Stopping</strong>: a workspace admin can turn an automation
            off or delete it at any time in Control Room. Work already done in
            your tools, such as projects, tasks and invites, stays there.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Google User Data</h2>
        <p>
          BossDrop&apos;s use and transfer to any other app of information
          received from Google APIs will adhere to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p>
          Data we receive from Google, including Gmail, Google Calendar and
          Google Meet, is used only to provide and improve the features described in this
          policy. It is not used for advertising, it is not sold, people at
          BossDrop do not read it except with your permission, for security
          purposes, or to comply with law, and it is never used to develop,
          improve or train generalized AI or machine learning models.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">How We Use Information</h2>
        <ul className="bullets">
          <li>Provide, secure, and maintain the Service and integrations.</li>
          <li>
            Generate AI summaries, plans, assignments, escalations, and related
            outputs you request or configure.
          </li>
          <li>
            Learn how your team runs, so BossDrop can time check-ins and
            escalations (see Team Memory).
          </li>
          <li>Personalize features, recommend automations, and improve UX.</li>
          <li>Monitor abuse, ensure compliance, and protect our users.</li>
          <li>
            Communicate about updates, security, and product announcements.
          </li>
          <li>
            Understand who visits our website, measure and target our
            advertising, and follow up with people who may be interested in
            BossDrop.
          </li>
          <li>
            De-identify and aggregate data to improve performance and
            reliability.
          </li>
        </ul>
        <p className="muted">
          We do not use Customer Data, including data from the tools you
          connect, to train AI models.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Sharing</h2>
        <ul className="bullets">
          <li>
            <strong>Service Providers/Sub-processors</strong>: cloud hosting,
            storage, logging/monitoring, analytics, email delivery, customer
            support, and AI model providers (e.g., OpenAI/Anthropic/others) used
            to generate outputs per your requests.
          </li>
          <li>
            <strong>Tools on Our Website</strong>: the companies named in that
            section receive what their tools collect directly from your browser.
          </li>
          <li>
            <strong>Third-Party Services You Connect</strong>: we share or write
            back data only as needed to deliver configured automations (e.g.,
            posting summaries into Slack or Microsoft Teams, creating or
            updating projects and tasks in your task tool, or scheduling a
            calendar event).
          </li>
          <li>
            <strong>People Your Automations Contact</strong>: invites and emails
            your organization sets up in a custom automation go to the people it
            names, such as a client on a deal.
          </li>
          <li>
            <strong>Legal/Compliance</strong>: to comply with law, protect
            rights and safety, or in a business transfer (merger, acquisition).
          </li>
        </ul>
        <p className="muted">
          We maintain a list of material sub-processors upon request and will
          provide notice of material changes where required.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Data Retention & Deletion</h2>
        <ul className="bullets">
          <li>
            Customer Data is retained for the duration of the subscription and
            then deleted or anonymized per admin-configured retention settings.
          </li>
          <li>
            Following termination, we delete or anonymize within a reasonable
            period (typically 30 to 90 days) unless we are legally required to
            retain data (e.g., fraud prevention, accounting).
          </li>
          <li>
            You may export certain data via in-product tools or by contacting{" "}
            <a href="mailto:support@bossdrop.ai">support@bossdrop.ai</a>.
            Workspace admins can export everything BossDrop has learned about
            how your team runs at any time from the Memory page.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">Security</h2>
        <p>
          We use industry-standard security controls (encryption in transit,
          access controls, least-privilege design, monitoring). No method of
          transmission or storage is 100% secure. We encourage strong passwords
          and MFA on connected tools and identity providers.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">International Transfers</h2>
        <p>
          We may process data in the United States and other countries. Where
          required, we use appropriate transfer mechanisms (e.g., Standard
          Contractual Clauses) and supplementary safeguards.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Your Privacy Choices</h2>
        <ul className="bullets">
          <li>
            Allow or refuse the tools on our website, one kind at a time, under
            Tools on Our Website above.
          </li>
          <li>Disconnect integrations or change access scopes at any time.</li>
          <li>
            Connect your mailbox for email tracking, or stop it, at any time.
          </li>
          <li>
            Opt out of AI notes for a meeting you are invited to, with the
            link in the email from ai@bossdrop.ai.
          </li>
          <li>
            See the lines about your own timing on the Memory page, and ask a
            workspace admin to correct them.
          </li>
          <li>
            If you are a workspace admin, correct, remove, export or clear what
            BossDrop has learned on the Memory page.
          </li>
          <li>
            If you are a workspace admin, turn off or delete a custom automation
            in Control Room.
          </li>
          <li>Configure retention windows and workspace visibility.</li>
          <li>Opt in/out of marketing communications.</li>
          <li>Request export, correction, or deletion (subject to law).</li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">GDPR/UK & California Rights</h2>
        <ul className="bullets">
          <li>
            <strong>EU/UK</strong>: legal bases include contract performance,
            legitimate interests (e.g., to secure and improve the Service), and
            consent, which is what we rely on for the tools on our website. You
            can withdraw that consent at any time under Tools on Our Website.
            Individuals may have rights of access, rectification, erasure,
            restriction, objection, and portability.
          </li>
          <li>
            <strong>California</strong>: we do not sell personal information for
            money. But the tools on our website send data from your browser to
            other companies, and some of that, especially the Meta Pixel and
            visitor identification, may count as “selling” or “sharing” under the
            CCPA/CPRA. To opt out, use Your privacy choices under Tools on Our
            Website, or turn on Global Privacy Control in your browser, which we
            honor automatically. You may also request access, deletion, and
            correction.
          </li>
        </ul>
        <p className="muted">
          Requests can be submitted to{" "}
          <a href="mailto:support@bossdrop.ai">support@bossdrop.ai</a>. We will
          verify identity before fulfilling a request.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Children</h2>
        <p>
          The Service is not directed to children under 16. We do not knowingly
          collect personal information from children.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Changes</h2>
        <p>
          We may update this Policy from time to time. Material changes will be
          posted on this page and, where appropriate, notified to admins.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">Contact</h2>
        <p>
          Questions or requests:{" "}
          <a href="mailto:info@bossdrop.ai">info@bossdrop.ai</a>.
        </p>
      </div>
    </section>
  );
}
